import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, MinusIcon, PlusIcon, ScanIcon, XIcon } from 'lucide-react';
import { useZoomPan } from './useZoomPan';
export interface LightboxItem {
  src: string;
  alt: string;
  caption: string;
}
interface LightboxProps {
  items: LightboxItem[];
  /** Index of the open item, or null when closed. */
  index: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
  label: string;
  counterNoun?: string;
}

/** Window event other UI (the sticky purchase bar) listens to. */
export const LIGHTBOX_EVENT = 'wfp-lightbox-toggle';
const iconButton = 'flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 ease-out hover:bg-white/20 disabled:opacity-30 disabled:hover:bg-white/10 focus:outline-none focus-visible:ring-4 focus-visible:ring-olive-light/70';

/**
 * Full-screen viewer for original high-resolution pages.
 * Pinch / wheel / double-tap / buttons to zoom, drag to pan, swipe or arrow
 * keys to change image, Escape to close. Focus is trapped and restored.
 */
export function Lightbox({
  items,
  index,
  onClose,
  onNavigate,
  label,
  counterNoun = 'Image'
}: LightboxProps) {
  const open = index !== null;
  const current = index !== null ? items[index] : null;
  const count = items.length;
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const go = useCallback((direction: 1 | -1) => {
    if (index === null) return;
    const next = index + direction;
    if (next >= 0 && next < count) onNavigate(next);
  }, [index, count, onNavigate]);
  const zoom = useZoomPan(stageRef, imageRef, {
    onSwipe: go
  });
  const {
    reset
  } = zoom;
  useEffect(() => {
    reset();
  }, [index, reset]);
  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.dispatchEvent(new CustomEvent(LIGHTBOX_EVENT, {
      detail: true
    }));
    const frame = window.requestAnimationFrame(() => closeRef.current?.focus());
    return () => {
      window.cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
      window.dispatchEvent(new CustomEvent(LIGHTBOX_EVENT, {
        detail: false
      }));
      previousFocus?.focus?.();
    };
  }, [open]);
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
      case 'ArrowRight':
        event.preventDefault();
        go(1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        go(-1);
        break;
      case '+':
      case '=':
        zoom.zoomIn();
        break;
      case '-':
        zoom.zoomOut();
        break;
      case 'Tab':
        {
          const all = dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])');
          const nodes = Array.from(all ?? []).filter(node => node.getClientRects().length > 0 && getComputedStyle(node).visibility !== 'hidden');
          if (nodes.length === 0) return;
          const first = nodes[0];
          const last = nodes[nodes.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
          break;
        }
      default:
        break;
    }
  };
  if (typeof document === 'undefined') return null;
  return createPortal(<AnimatePresence data-id="element-80" data-mp="2sqrjltfhwpov">
      {open && current && index !== null && <motion.div ref={dialogRef} role="dialog" aria-modal="true" aria-label={`${label} — ${current.caption}`} onKeyDown={onKeyDown} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} className="fixed inset-0 z-50 flex flex-col bg-charcoal-deep/[0.97] text-white" style={{
      paddingTop: 'env(safe-area-inset-top)',
      paddingBottom: 'env(safe-area-inset-bottom)'
    }} data-id="element-81" data-mp="2sodmlf1993eo">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 px-4 py-2 sm:px-6" data-id="element-82" data-mp="2luyl5wpdvvbm">
            <div className="min-w-0" aria-live="polite" data-id="element-83" data-mp="276u38uy5nntw">
              <p className="label-tech text-olive-light" data-id="element-84" data-mp="2c3c4vly6v620">
                {counterNoun} {index + 1} of {count}
              </p>
              <p className="truncate text-[1rem] font-semibold leading-tight" data-id="element-85" data-mp="1y4ulg0fbgfpw">
                {current.caption}
              </p>
            </div>
            <button ref={closeRef} type="button" onClick={onClose} aria-label="Close viewer" className={iconButton} data-id="element-86" data-mp="1jhm1aa41501s">
              <XIcon className="h-5 w-5" aria-hidden data-id="element-87" data-mp="1ej2keo4amega" />
            </button>
          </div>

          <div ref={stageRef} {...zoom.handlers} className="relative min-h-0 flex-1 touch-none select-none overflow-hidden" style={{
        cursor: zoom.scale > 1 ? zoom.interacting ? 'grabbing' : 'grab' : 'zoom-in'
      }} data-id="element-88" data-mp="1sitq81knooj7">
            <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-6" data-id="element-89" data-mp="22e57aqjje7u4">
              <img ref={imageRef} key={current.src} src={current.src} alt={current.alt} draggable={false} className="max-h-full max-w-full rounded-[2px] object-contain" style={{
            transform: `translate3d(${zoom.offset.x}px, ${zoom.offset.y}px, 0) scale(${zoom.scale})`,
            transition: zoom.interacting ? 'none' : 'transform 180ms cubic-bezier(0.23, 1, 0.32, 1)'
          }} data-id="element-90" data-mp="1xfanbl9u2d8f" />
            </div>

            <button type="button" onClick={() => go(-1)} disabled={index === 0} aria-label="Previous" onPointerDown={event => event.stopPropagation()} className={`${iconButton} absolute left-4 top-1/2 hidden -translate-y-1/2 md:flex`} data-id="element-91" data-mp="1iumjwzieic2k">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-92" data-mp="1wn2hvlwg3jwm" />
            </button>
            <button type="button" onClick={() => go(1)} disabled={index === count - 1} aria-label="Next" onPointerDown={event => event.stopPropagation()} className={`${iconButton} absolute right-4 top-1/2 hidden -translate-y-1/2 md:flex`} data-id="element-93" data-mp="21i83ejlxqwk6">
              <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-94" data-mp="g6geamdtxywf" />
            </button>
          </div>

          <div className="shrink-0 border-t border-white/10 px-3 pb-2 pt-2 sm:px-6" data-id="element-95" data-mp="8mxfp7s3rx2q">
            <div className="flex items-center justify-between gap-2" data-id="element-96" data-mp="3wgzxh0t7zd7">
              <button type="button" onClick={() => go(-1)} disabled={index === 0} aria-label="Previous" className={`${iconButton} md:invisible`} data-id="element-97" data-mp="3v5y3mqxd1oq">
                <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-98" data-mp="rlt9wj3yobgk" />
              </button>

              <div className="flex items-center gap-2" data-id="element-99" data-mp="mmynl76sc4rp">
                <button type="button" onClick={zoom.zoomOut} disabled={!zoom.canZoomOut} aria-label="Zoom out" className={iconButton} data-id="element-100" data-mp="mnhwjyegjitl">
                  <MinusIcon className="h-5 w-5" aria-hidden data-id="element-101" data-mp="33bcb9a8yuxa3" />
                </button>
                <span className="w-12 text-center text-[14px] font-semibold tabular-nums text-parchment" data-id="element-102" data-mp="3399e869ec3vv">
                  {Math.round(zoom.scale * 100)}%
                </span>
                <button type="button" onClick={zoom.zoomIn} disabled={!zoom.canZoomIn} aria-label="Zoom in" className={iconButton} data-id="element-103" data-mp="12834w90ljuby">
                  <PlusIcon className="h-5 w-5" aria-hidden data-id="element-104" data-mp="x9rsbqix35q1" />
                </button>
                <button type="button" onClick={zoom.reset} disabled={!zoom.canZoomOut} aria-label="Fit to screen" className={iconButton} data-id="element-105" data-mp="1kzvtlr8mutdv">
                  <ScanIcon className="h-5 w-5" aria-hidden data-id="element-106" data-mp="1b99nqoqpxh8d" />
                </button>
              </div>

              <button type="button" onClick={() => go(1)} disabled={index === count - 1} aria-label="Next" className={`${iconButton} md:invisible`} data-id="element-107" data-mp="1g7kvmtxgdhny">
                <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-108" data-mp="3tfvl8pt3ksum" />
              </button>
            </div>
            <p className="mt-1.5 text-center text-[0.78rem] text-muted" data-id="element-109" data-mp="3oh0ristldjvq">
              Pinch, scroll or double-tap to zoom · drag to pan
            </p>
          </div>
        </motion.div>}
    </AnimatePresence>, document.body);
}